import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FormDialog } from "../form";
import { DataTable } from "../data";
import { EmptyState, TableSkeleton } from "../feedback";
import { getUploadFileUrl } from "../../utils/photoUrl";

export default function VendorAttachmentsDialog({
  open,
  onClose,
  vendor,
  attachments,
  loading,
  uploading,
  onUpload,
  onDelete,
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  const columns = [
    {
      id: "filename",
      label: "Filename",
      primary: true,
      render: (a) => a.filename,
    },
    {
      id: "type",
      label: "Type",
      render: (a) => a.contentType ?? "—",
    },
    {
      id: "uploaded",
      label: "Uploaded",
      render: (a) =>
        a.uploadedAt ? new Date(a.uploadedAt).toLocaleString() : "—",
    },
    {
      id: "actions",
      label: "",
      align: "right",
      hideLabelOnMobile: true,
      render: (a) => (
        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
        >
          {a.contentType?.startsWith("image/") && (
            <IconButton
              size="small"
              title="Preview"
              onClick={() => setPreviewUrl(getUploadFileUrl(a.key))}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            title="Download"
            component="a"
            href={getUploadFileUrl(a.key)}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Delete"
            onClick={() => onDelete(a.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <FormDialog
        open={open}
        onClose={onClose}
        title="Attachments"
        subtitle={vendor?.name}
        cancelLabel="Close"
        submitLabel={null}
        onSubmit={null}
        maxWidth="md"
      >
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              size="small"
              component="label"
              startIcon={<UploadFileIcon sx={{ fontSize: 18 }} />}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Upload attachment"}
              <input
                hidden
                type="file"
                accept="image/*,.pdf,.doc,.docx"
                onChange={onUpload}
              />
            </Button>
          </Box>
          {loading ? (
            <TableSkeleton rows={3} columns={4} />
          ) : (
            <DataTable
              columns={columns}
              rows={attachments}
              rowKey="id"
              emptyState={
                <EmptyState
                  title="No attachments yet"
                  description="Upload menus, certificates, or contracts for this vendor."
                  dense
                />
              }
            />
          )}
        </Stack>
      </FormDialog>

      <Dialog
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl("")}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ position: "relative", p: 0 }}>
          <IconButton
            onClick={() => setPreviewUrl("")}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "background.paper",
              zIndex: 1,
              "&:hover": { bgcolor: "background.default" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={previewUrl}
            alt="Attachment preview"
            sx={{ display: "block", width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
